import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Save, Upload, Trash2, ImageIcon, Download, Copy, RefreshCw } from "lucide-react";
import AccessDeniedPreview from "@/components/AccessDeniedPreview";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const absUrl = (p) => {
  if (!p) return "";
  if (/^https?:/i.test(p)) return p;
  return `${BACKEND_URL}${p}`;
};

export default function SettingsManager() {
  const [form, setForm] = useState({
    site_title: "",
    site_subtitle: "",
    brand_color: "#002FA7",
    icons_per_row: 6,
    use_category_color: false,
    bg_image_path: "",
    site_logo_path: "",
    site_logo_dark_path: "",
    denied_title: "Accès refusé",
    denied_subtitle: "",
    denied_message: "",
    denied_show_ip: true,
    denied_contact: "",
    denied_bg_image_path: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exportToken, setExportToken] = useState("");
  const [zipBuilding, setZipBuilding] = useState(false);
  const logoInputRef = useRef(null);
  const logoDarkInputRef = useRef(null);
  const bgInputRef = useRef(null);
  const deniedBgInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: tk }] = await Promise.all([
        api.get("/admin/settings"),
        api.get("/admin/export/token"),
      ]);
      setForm(data);
      setExportToken(tk.export_token);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", form);
      setForm(data);
      toast.success("Réglages enregistrés.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/settings/upload?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const keyMap = { logo: "site_logo_path", "logo-dark": "site_logo_dark_path", bg: "bg_image_path", "denied-bg": "denied_bg_image_path" };
      setForm((f) => ({ ...f, [keyMap[kind]]: data.path }));
      toast.success("Image mise à jour.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const removeImage = async (kind) => {
    const keyMap = { logo: "site_logo_path", "logo-dark": "site_logo_dark_path", bg: "bg_image_path", "denied-bg": "denied_bg_image_path" };
    const key = keyMap[kind];
    setForm((f) => ({ ...f, [key]: "" }));
    try {
      await api.put("/admin/settings", { [key]: "" });
      toast.success("Image retirée.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const downloadPersonalizedZip = async () => {
    setZipBuilding(true);
    try {
      const resp = await api.post("/admin/export/php-zip", {}, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "netkom-portal-personnalise.zip";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("ZIP téléchargé.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setZipBuilding(false);
    }
  };

  const downloadJsonBackup = async () => {
    try {
      const { data: tk } = await api.get("/admin/export/token");
      const resp = await fetch(`${BACKEND_URL}/api/export/data`, {
        headers: { "X-Export-Token": tk.export_token },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `netkom-backup-${stamp}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Sauvegarde JSON téléchargée.");
    } catch (e) {
      toast.error(e.message || "Erreur de téléchargement");
    }
  };

  const rotateToken = async () => {
    if (!window.confirm("Générer un nouveau token ? L'ancien sera révoqué.")) return;
    try {
      const { data } = await api.post("/admin/export/token/rotate");
      setExportToken(data.export_token);
      toast.success("Nouveau token généré.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copié.`),
      () => toast.error("Impossible de copier.")
    );
  };

  const previewUrl = BACKEND_URL || "";

  if (loading) {
    return <p className="text-muted-foreground text-sm">Chargement…</p>;
  }

  return (
    <div className="space-y-8" data-testid="admin-settings-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
          // Configuration
        </p>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Réglages du portail</h1>
      </div>

      <form onSubmit={save} className="space-y-8 max-w-2xl">
        {/* Identité */}
        <section className="border border-border bg-card rounded-sm p-6 space-y-4">
          <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Identité</h2>

          <div className="space-y-2">
            <Label htmlFor="site_title">Titre du portail</Label>
            <Input
              id="site_title"
              data-testid="settings-site-title"
              value={form.site_title}
              onChange={(e) => setForm({ ...form, site_title: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="site_subtitle">Sous-titre</Label>
            <Input
              id="site_subtitle"
              data-testid="settings-site-subtitle"
              value={form.site_subtitle}
              onChange={(e) => setForm({ ...form, site_subtitle: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Logo du portail (mode clair)</Label>
            <div className="flex items-center gap-4 flex-wrap">
              {form.site_logo_path && (
                <img
                  src={absUrl(form.site_logo_path)}
                  alt="logo"
                  className="h-12 bg-muted border border-border rounded p-1"
                  data-testid="settings-logo-preview"
                />
              )}
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={(e) => upload("logo", e.target.files?.[0])}
                data-testid="settings-logo-input"
              />
              <Button type="button" variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} data-testid="settings-upload-logo">
                <Upload className="h-4 w-4 mr-2" /> Téléverser
              </Button>
              {form.site_logo_path && (
                <Button type="button" variant="ghost" size="sm" onClick={() => removeImage("logo")} data-testid="settings-remove-logo">
                  <Trash2 className="h-4 w-4 mr-2" /> Retirer
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Logo du portail (mode sombre)</Label>
            <p className="text-xs text-muted-foreground">Optionnel — si vide, le logo du mode clair est utilisé en mode sombre.</p>
            <div className="flex items-center gap-4 flex-wrap">
              {form.site_logo_dark_path && (
                <img
                  src={absUrl(form.site_logo_dark_path)}
                  alt="logo sombre"
                  className="h-12 bg-zinc-900 border border-border rounded p-1"
                  data-testid="settings-logo-dark-preview"
                />
              )}
              <input
                ref={logoDarkInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={(e) => upload("logo-dark", e.target.files?.[0])}
                data-testid="settings-logo-dark-input"
              />
              <Button type="button" variant="outline" size="sm" onClick={() => logoDarkInputRef.current?.click()} data-testid="settings-upload-logo-dark">
                <Upload className="h-4 w-4 mr-2" /> Téléverser
              </Button>
              {form.site_logo_dark_path && (
                <Button type="button" variant="ghost" size="sm" onClick={() => removeImage("logo-dark")} data-testid="settings-remove-logo-dark">
                  <Trash2 className="h-4 w-4 mr-2" /> Retirer
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Image de fond de la page d&apos;accueil</Label>
            <div className="flex items-center gap-4 flex-wrap">
              {form.bg_image_path ? (
                <img
                  src={absUrl(form.bg_image_path)}
                  alt="fond"
                  className="h-16 w-28 object-cover border border-border rounded"
                  data-testid="settings-bg-preview"
                />
              ) : (
                <div className="h-16 w-28 border border-dashed border-border rounded flex items-center justify-center text-muted-foreground">
                  <ImageIcon className="h-5 w-5" />
                </div>
              )}
              <input
                ref={bgInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => upload("bg", e.target.files?.[0])}
                data-testid="settings-bg-input"
              />
              <Button type="button" variant="outline" size="sm" onClick={() => bgInputRef.current?.click()} data-testid="settings-upload-bg">
                <Upload className="h-4 w-4 mr-2" /> Téléverser
              </Button>
              {form.bg_image_path && (
                <Button type="button" variant="ghost" size="sm" onClick={() => removeImage("bg")} data-testid="settings-remove-bg">
                  <Trash2 className="h-4 w-4 mr-2" /> Retirer
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Une superposition est appliquée pour garder la lisibilité. Format paysage recommandé (1920×1080).
            </p>
          </div>
        </section>

        {/* Mise en page */}
        <section className="border border-border bg-card rounded-sm p-6 space-y-4">
          <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Mise en page</h2>

          <div className="space-y-2">
            <Label htmlFor="icons_per_row">
              Nombre d&apos;icônes par ligne (desktop) : <strong className="text-primary text-lg">{form.icons_per_row}</strong>
            </Label>
            <input
              id="icons_per_row"
              type="range"
              min={2}
              max={12}
              step={1}
              value={form.icons_per_row}
              onChange={(e) => setForm({ ...form, icons_per_row: parseInt(e.target.value, 10) })}
              className="w-full"
              data-testid="settings-icons-per-row"
            />
            <p className="text-xs text-muted-foreground">
              Sur mobile, le nombre s&apos;adapte automatiquement. La grille reste centrée ; la dernière ligne s&apos;aligne à gauche si elle est incomplète.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="brand_color">Couleur de marque</Label>
            <div className="flex items-center gap-3">
              <input
                id="brand_color"
                type="color"
                value={form.brand_color}
                onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
                className="h-10 w-12 rounded border border-border cursor-pointer"
                data-testid="settings-brand-color"
              />
              <Input
                value={form.brand_color}
                onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
                className="font-mono w-40"
              />
            </div>
          </div>

          <div className="flex items-center justify-between bg-muted/50 p-3 rounded">
            <div>
              <Label className="cursor-pointer">Utiliser la couleur de la catégorie</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Chaque tuile prend la couleur de sa catégorie.
              </p>
            </div>
            <Switch
              checked={form.use_category_color}
              onCheckedChange={(v) => setForm({ ...form, use_category_color: v })}
              data-testid="settings-use-cat-color"
            />
          </div>
        </section>

        {/* Page Accès refusé */}
        <section className="border border-border bg-card rounded-sm p-6 space-y-4">
          <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Page « Accès refusé » (IP non autorisée)</h2>

          <div className="space-y-2">
            <Label htmlFor="denied_title">Titre</Label>
            <Input
              id="denied_title"
              value={form.denied_title}
              onChange={(e) => setForm({ ...form, denied_title: e.target.value })}
              placeholder="Accès refusé"
              data-testid="settings-denied-title"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="denied_subtitle">Sous-titre</Label>
            <Input
              id="denied_subtitle"
              value={form.denied_subtitle}
              onChange={(e) => setForm({ ...form, denied_subtitle: e.target.value })}
              placeholder="Votre adresse IP n'est pas autorisée"
              data-testid="settings-denied-subtitle"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="denied_message">Message complet</Label>
            <textarea
              id="denied_message"
              value={form.denied_message}
              onChange={(e) => setForm({ ...form, denied_message: e.target.value })}
              rows={4}
              className="w-full border border-input bg-background rounded-md p-2 text-sm font-sans resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="settings-denied-message"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="denied_contact">Contact (email ou texte libre — optionnel)</Label>
            <Input
              id="denied_contact"
              value={form.denied_contact}
              onChange={(e) => setForm({ ...form, denied_contact: e.target.value })}
              placeholder="admin@exemple.fr ou « Appeler le 01 23 45 67 89 »"
              data-testid="settings-denied-contact"
            />
            <p className="text-xs text-muted-foreground">
              Si vous renseignez un email, un bouton « Contacter l&apos;administrateur » s&apos;affichera.
            </p>
          </div>

          <div className="flex items-center justify-between bg-muted/50 p-3 rounded">
            <div>
              <Label className="cursor-pointer">Afficher l&apos;IP du visiteur</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Pratique pour que le visiteur transmette son IP à l&apos;administrateur.
              </p>
            </div>
            <Switch
              checked={form.denied_show_ip}
              onCheckedChange={(v) => setForm({ ...form, denied_show_ip: v })}
              data-testid="settings-denied-show-ip"
            />
          </div>

          <div className="space-y-2 pt-2 border-t border-border">
            <Label>Image de fond — page Accès refusé</Label>
            <p className="text-xs text-muted-foreground">
              Laissez vide pour réutiliser le fond de la page d&apos;accueil.
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              {form.denied_bg_image_path ? (
                <img
                  src={absUrl(form.denied_bg_image_path)}
                  alt="fond accès refusé"
                  className="h-16 w-28 object-cover border border-border rounded"
                  data-testid="settings-denied-bg-preview"
                />
              ) : (
                <div className="h-16 w-28 border border-dashed border-border rounded flex items-center justify-center text-muted-foreground">
                  <ImageIcon className="h-5 w-5" />
                </div>
              )}
              <input
                ref={deniedBgInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => upload("denied-bg", e.target.files?.[0])}
                data-testid="settings-denied-bg-input"
              />
              <Button type="button" variant="outline" size="sm" onClick={() => deniedBgInputRef.current?.click()} data-testid="settings-upload-denied-bg">
                <Upload className="h-4 w-4 mr-2" /> Téléverser
              </Button>
              {form.denied_bg_image_path && (
                <Button type="button" variant="ghost" size="sm" onClick={() => removeImage("denied-bg")} data-testid="settings-remove-denied-bg">
                  <Trash2 className="h-4 w-4 mr-2" /> Retirer
                </Button>
              )}
            </div>
          </div>

          <div className="pt-3 border-t border-border space-y-2">
            <Label>Aperçu en direct</Label>
            <p className="text-xs text-muted-foreground">
              Cet aperçu reflète vos modifications en temps réel. Les changements ne sont
              actifs sur le site que lorsque vous cliquez sur « Enregistrer ».
            </p>
            <AccessDeniedPreview settings={form} />
          </div>
        </section>

        {/* Export / Mise à jour */}
        <section className="border border-border bg-card rounded-sm p-6 space-y-4">
          <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Export / Mise à jour</h2>

          <div className="space-y-2">
            <Label>Télécharger la version PHP avec vos données pré-remplies</Label>
            <p className="text-xs text-muted-foreground">
              Génère un ZIP contenant tout le code PHP + un fichier <code>seed.sql</code> avec
              vos liens, catégories, annonces, IP autorisées et réglages actuels. À déployer
              sur votre hébergement mutualisé via <code>install.php</code>.
            </p>
            <Button
              type="button"
              onClick={downloadPersonalizedZip}
              disabled={zipBuilding}
              data-testid="settings-download-zip"
            >
              <Download className="h-4 w-4 mr-2" />
              {zipBuilding ? "Préparation du ZIP…" : "Télécharger le ZIP personnalisé"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={downloadJsonBackup}
              data-testid="settings-download-json"
              className="ml-2"
            >
              <Download className="h-4 w-4 mr-2" /> Sauvegarde JSON
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              💾 <strong>Vos données sont stockées en permanence</strong> sur l&apos;infrastructure Emergent.
              Téléchargez régulièrement une sauvegarde JSON pour la conserver localement.
            </p>
          </div>

          <div className="border-t border-border pt-4 space-y-2">
            <Label>Synchronisation continue (depuis une instance PHP installée)</Label>
            <p className="text-xs text-muted-foreground">
              Une fois la version PHP installée sur votre serveur, allez dans son backoffice →
              <strong> Mise à jour</strong>. Collez l&apos;URL et le token ci-dessous pour récupérer
              les dernières données.
            </p>

            <div className="space-y-2 pt-2">
              <Label htmlFor="preview_url">URL de cette instance preview</Label>
              <div className="flex gap-2">
                <Input
                  id="preview_url"
                  value={previewUrl}
                  readOnly
                  className="font-mono text-xs"
                  data-testid="settings-preview-url"
                />
                <Button type="button" variant="outline" size="sm" onClick={() => copyToClipboard(previewUrl, "URL")}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <Label htmlFor="export_token">Token d&apos;export</Label>
              <div className="flex gap-2">
                <Input
                  id="export_token"
                  value={exportToken}
                  readOnly
                  className="font-mono text-xs"
                  data-testid="settings-export-token"
                />
                <Button type="button" variant="outline" size="sm" onClick={() => copyToClipboard(exportToken, "Token")}>
                  <Copy className="h-4 w-4" />
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={rotateToken} data-testid="settings-rotate-token" title="Générer un nouveau token">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Gardez ce token confidentiel. Il donne un accès lecture seule aux données d&apos;export.
              </p>
            </div>
          </div>
        </section>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} data-testid="settings-save-btn">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </form>
    </div>
  );
}
